<script setup lang="ts">
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { signInWithEmail, signInWithGoogle } from "../lib/firebase";
import { useAdminSession } from "../lib/session";

const route = useRoute();
const router = useRouter();
const session = useAdminSession();
const email = ref("");
const password = ref("");
const busy = ref(false);
const message = ref("");

async function finishLogin(action: () => Promise<void>) {
  busy.value = true;
  message.value = "";
  try {
    await action();
    await session.loadSession(true);
    const redirect = typeof route.query.redirect === "string" ? route.query.redirect : "/orders";
    await router.replace(redirect);
  } catch (error) {
    message.value = error instanceof Error ? error.message : "目前無法登入。";
  } finally {
    busy.value = false;
  }
}

async function switchAccount() {
  await session.logOut();
  message.value = "";
}
</script>

<template>
  <main class="login-stage">
    <section class="login-identity" aria-labelledby="login-title">
      <div class="login-brand">
        <span class="brand-seal large" aria-hidden="true">清<br />和</span>
        <span>清和書院 · 營運台</span>
      </div>
      <div>
        <p class="eyebrow light">Operations console</p>
        <h1 id="login-title">讓每一筆款項，<br />都有清楚的去向。</h1>
        <p class="login-intro">查明付款、報告產生與交付狀態。所有變更都會留下操作原因與紀錄。</p>
      </div>
      <p class="security-note">僅限經授權的清和書院人員使用</p>
    </section>

    <section class="login-panel">
      <div class="login-form-wrap">
        <p class="eyebrow">安全登入</p>
        <h2>進入營運台</h2>
        <p class="form-help">使用已加入管理名單的 Firebase 帳號。</p>

        <div v-if="session.firebaseUser.value && session.accessError.value" class="access-denied" role="alert">
          <strong>此帳號尚未開通</strong>
          <span>{{ session.firebaseUser.value.email }}</span>
          <p>{{ session.accessError.value }}</p>
          <button class="text-button" type="button" @click="switchAccount">改用其他帳號</button>
        </div>

        <template v-else>
          <button class="google-button" type="button" :disabled="busy" @click="finishLogin(signInWithGoogle)">
            <span aria-hidden="true">G</span> 使用 Google 帳號登入
          </button>
          <div class="or-line"><span>或使用 Email</span></div>
          <form @submit.prevent="finishLogin(() => signInWithEmail(email, password))">
            <label>Email<input v-model="email" type="email" autocomplete="username" required /></label>
            <label>密碼<input v-model="password" type="password" autocomplete="current-password" required /></label>
            <button class="primary-button full" type="submit" :disabled="busy">
              {{ busy ? "驗證中…" : "登入" }}
            </button>
          </form>
        </template>
        <p v-if="message" class="form-error" role="alert">{{ message }}</p>
      </div>
    </section>
  </main>
</template>
